import { FC, useEffect, createRef } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import Head from 'next/head'
import parse from 'html-react-parser'
import ReadMore from './read-more/ReadMore'
import { ButtonArrow } from 'components/button/assets'
import { Bolt } from './assets'
import { Chain } from './assets'
import { Direction } from './assets'
import { Employee } from './types'
import Link from 'next/link'
import { routes } from 'utils/routes'
import { useTranslation } from 'react-i18next'
import { Language } from 'types/language'
import { AboutProps } from './types'
import styles from './About.module.scss'
import { TITLE_POSTFIX } from 'constants/seo'

const About: FC<AboutProps> = ({ employees }) => {
  // i18n
  const { t } = useTranslation('about')
  const { locale: language } = useRouter()

  // Ref
  const teamRef = createRef<HTMLDivElement>()

  /**
   * Triggered on every render
   */
  useEffect(() => {
    // Scroll to 'team' section if hash has been set
    const hash = window.location.hash
    if (hash === '#team' && teamRef.current) window.scrollTo(0, teamRef.current.offsetTop)
  })

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {t('seoTitle')} {TITLE_POSTFIX}
        </title>
        <meta name="description" content={t('seoDescription')} />
        <meta property="og:title" content={`${t('seoTitle')} ${TITLE_POSTFIX}`} key="title" />
        <meta property="og:description" content={t('seoDescription')} />
      </Head>

      <div className={styles.about}>
        <div className={styles.fold}>
          <div className="container">
            <div className={styles.intro}>
              <p className={classNames(styles.subtitle, styles['has-text-centered-tablet'], 'margin-bottom-s margin-top-none')}>
                {t('videoSubtitle', 'Een vernieuwend peer-to-peer energiemodel')}
              </p>

              <h1 className={classNames(styles.title, styles['has-text-centered-tablet'], 'margin-top-none margin-bottom-l')}>
                {t('videoTitle', 'Wij geloven in een nieuwe energiemarkt')}
              </h1>

              <div className="video-16-9">
                {language === Language.DUTCH && (
                  <iframe
                    title="Waarom energie via Bolt?"
                    src="https://player.vimeo.com/video/349628372?api=1&loop=1"
                    frameBorder="0"
                    allowFullScreen
                  />
                )}

                {language === Language.FRENCH && (
                  <iframe
                    title="Pourquoi acheter son énergie via Bolt"
                    src="https://player.vimeo.com/video/657091827?api=1&loop=1"
                    frameBorder="0"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.values}>
          <div className="container">
            <p className={classNames(styles.subtitle, styles['has-text-centered-tablet'], 'margin-bottom-s margin-top-none')}>
              {t('benefitsSubtitle', 'Wij willen zorgen voor een groener België')}
            </p>

            <h2 className={classNames(styles.title, styles['has-text-centered-tablet'], 'margin-top-none margin-bottom-l')}>
              {t('benefitsTitle', 'Bolt ondersteunt een duurzame toekomst')}
            </h2>

            <div className={classNames(styles['mission-values'], 'margin-bottom-l-desktop margin-bottom-m-mobile')}>
              <div className={styles.value}>
                <div>
                  <Direction className={styles.icon} />

                  <h3 className={classNames(styles.title, 'margin-bottom-s')}>{t('benefit1Title', '100% lokale groene energie')}</h3>

                  <p className={classNames(styles.description, 'margin-bottom-none')}>
                    {t(
                      'benefit1Text',
                      'We willen de lokale productie van groene energie een serieuze duw in de rug geven. Onze klanten kunnen hun groenestroomproducent zélf uitkiezen en er 100% zeker van zijn: het geld dat ze investeren in energie komt terecht bij de mensen die met hun kleinschalige en lokale initiatieven nú al hun schouders zetten onder een groener België.'
                    )}
                  </p>
                </div>
              </div>
              <div className={styles.value}>
                <div>
                  <Bolt className={styles.icon} />

                  <h3 className={classNames(styles.title, 'margin-bottom-s')}>{t('benefit2Title', 'Transparant &amp; eerlijk')}</h3>

                  <p className={classNames(styles.description, 'margin-bottom-none')}>
                    {t(
                      'benefit2Text',
                      'Als jonge ondernemers in de energiesector merken we dat energieconsumenten niet de transparantie, service en het vertrouwen krijgen dat ze verdienen. Wij willen een alternatief brengen door groene energie eenvoudiger en goedkoper aan te bieden.'
                    )}
                  </p>
                </div>
              </div>
              <div className={styles.value}>
                <div>
                  <Chain className={styles.icon} />

                  <h3 className={classNames(styles.title, 'margin-bottom-s')}>{t('benefit3Title', 'Ons oog op de toekomst')}</h3>

                  <p className={classNames(styles.description, 'margin-bottom-none')}>
                    {t(
                      'benefit3Text',
                      'We willen op een positieve manier en met een blik op de toekomst timmeren aan een groen België. Er wordt voldoende rond de pot gedraaid, het is dus tijd voor actie!'
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles['has-text-centered-tablet']}>
              <Link href={routes(language).producers}>
                <a className="btn is-primary is-inverse with-gradient is-inline-block with-arrow is-fullwidth-mobile">
                  {t('benefitsCTA', 'Ontdek onze producenten')}&nbsp;
                  <ButtonArrow className="arrow" />
                </a>
              </Link>
            </div>
          </div>
        </div>

        <div className={styles.team} id="team" ref={teamRef}>
          <div className="container">
            <h2 className={classNames(styles.title, styles['has-text-centered-tablet'], 'margin-top-none margin-bottom-l')}>
              {t('aboutTitle', 'A team is as good as its players')}
            </h2>
            <div className={styles['team-members']}>
              {employees.map((employee: Employee) => (
                <div key={employee.id}>
                  <div className={styles['team-member']}>
                    <div className={styles.image} style={{ backgroundImage: `url(${employee.image})` }} />
                    <div className={styles.content}>
                      <p className={styles.name}>{parse(employee.name)}</p>
                      <p className={styles['job-title']}>{employee.title}</p>
                      <ReadMore text={employee.description} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default About
